import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { tradingAPI } from '../../../../services/tradingAPI';
import '../../../../css/trading.css';

// ── Pure-SVG candlestick chart with optional portfolio overlay ─────────────
const CandlestickSVG = ({ data, portfolioData }) => {
  const containerRef = useRef(null);
  const [svgWidth, setSvgWidth] = useState(600);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) setSvgWidth(containerRef.current.offsetWidth);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  if (!data.length) return null;

  const PAD = { top: 12, right: 50, bottom: 36, left: 8 };
  const chartW = Math.max(svgWidth - PAD.left - PAD.right, 100);
  const chartH = 280;

  const allPrices = data.flatMap(d => [d.high, d.low]);
  const minP = Math.min(...allPrices) * 0.997;
  const maxP = Math.max(...allPrices) * 1.003;
  const priceToY = (p) => chartH - ((p - minP) / (maxP - minP)) * chartH;

  // Build portfolio overlay — normalise to same scale as SPY
  // We index-normalise: portfolio starts at SPY's first close and tracks % moves
  const portPoints = (() => {
    if (!portfolioData?.dates?.length || !portfolioData?.portfolio_values?.length) return [];
    const vals = portfolioData.portfolio_values;
    const dates = portfolioData.dates;
    if (!vals.length) return [];
    const base = vals[0];
    const spyBase = data[0]?.close || 1;
    const ohlcDates = new Set(data.map(d => d.time));
    return dates
      .map((d, i) => ({ date: d, val: vals[i] }))
      .filter(({ date }) => ohlcDates.has(date) || true) // include all, we'll interpolate x
      .map(({ date, val }, i) => {
        const norm = (val / base) * spyBase; // normalised to SPY scale
        const xFrac = i / (dates.length - 1 || 1);
        const cx = xFrac * (chartW - (chartW / data.length));
        const cy = priceToY(norm);
        return `${cx},${cy}`;
      });
  })();

  const step = chartW / data.length;
  const bodyW = Math.max(Math.floor(step * 0.6), 2);

  // Y-axis tick labels
  const yTicks = 5;
  const yTickVals = Array.from({ length: yTicks }, (_, i) =>
    minP + ((maxP - minP) * i) / (yTicks - 1)
  );

  // X-axis labels — show ~8 evenly spaced
  const xLabelInterval = Math.max(1, Math.floor(data.length / 8));

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Hover tooltip */}
      {hovered && (
        <div style={{
          position: 'absolute', top: 8, left: 16, pointerEvents: 'none',
          background: '#1a1a2e', border: '1px solid #333', borderRadius: 6,
          padding: '8px 12px', fontSize: '0.82em', color: '#ddd', zIndex: 10,
          lineHeight: 1.6,
        }}>
          <div style={{ color: '#a78bfa', marginBottom: 2 }}>{hovered.time}</div>
          <div>O: <b style={{ color: '#ddd' }}>${hovered.open.toFixed(2)}</b></div>
          <div>H: <b style={{ color: '#26a69a' }}>${hovered.high.toFixed(2)}</b></div>
          <div>L: <b style={{ color: '#ef5350' }}>${hovered.low.toFixed(2)}</b></div>
          <div>C: <b style={{ color: hovered.close >= hovered.open ? '#26a69a' : '#ef5350' }}>
            ${hovered.close.toFixed(2)}
          </b></div>
        </div>
      )}

      <svg
        width={svgWidth}
        height={chartH + PAD.top + PAD.bottom}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <g transform={`translate(${PAD.left},${PAD.top})`}>
          {/* Horizontal grid lines + Y labels */}
          {yTickVals.map((val) => {
            const y = priceToY(val);
            return (
              <g key={val}>
                <line x1={0} y1={y} x2={chartW} y2={y} stroke="#222" strokeDasharray="3 3" />
                <text x={chartW + 6} y={y + 4} fill="#777" fontSize={10} textAnchor="start">
                  ${Math.round(val)}
                </text>
              </g>
            );
          })}

          {/* Candles */}
          {data.map((d, i) => {
            const cx    = i * step + step / 2;
            const yH    = priceToY(d.high);
            const yL    = priceToY(d.low);
            const yO    = priceToY(d.open);
            const yC    = priceToY(d.close);
            const green = d.close >= d.open;
            const color = green ? '#26a69a' : '#ef5350';
            const bTop  = Math.min(yO, yC);
            const bH    = Math.max(Math.abs(yO - yC), 1);

            return (
              <g
                key={d.time}
                onMouseEnter={() => setHovered(d)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'crosshair' }}
              >
                {/* Wick */}
                <line x1={cx} y1={yH} x2={cx} y2={yL} stroke={color} strokeWidth={1} />
                {/* Body */}
                <rect
                  x={cx - bodyW / 2}
                  y={bTop}
                  width={bodyW}
                  height={bH}
                  fill={color}
                />
                {/* Invisible hit area */}
                <rect x={cx - step / 2} y={0} width={step} height={chartH} fill="transparent" />
              </g>
            );
          })}

          {/* Portfolio value overlay line */}
          {portPoints.length > 1 && (
            <polyline
              points={portPoints.join(' ')}
              fill="none"
              stroke="#FF7CDE"
              strokeWidth={2}
              strokeOpacity={0.85}
              strokeLinejoin="round"
            />
          )}

          {/* X-axis labels */}
          {data.map((d, i) => {
            if (i % xLabelInterval !== 0) return null;
            const cx = i * step + step / 2;
            return (
              <text
                key={d.time}
                x={cx}
                y={chartH + 18}
                fill="#777"
                fontSize={10}
                textAnchor="middle"
                transform={`rotate(-35, ${cx}, ${chartH + 18})`}
              >
                {d.time.slice(5)}
              </text>
            );
          })}

          {/* Legend */}
          {portPoints.length > 1 && (
            <g transform={`translate(${chartW - 160}, 8)`}>
              <rect width={150} height={44} rx={5} fill="rgba(13,8,37,0.8)" stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
              <line x1={8} y1={14} x2={24} y2={14} stroke="#26a69a" strokeWidth={2} />
              <rect x={8} y={10} width={6} height={8} fill="#26a69a" />
              <text x={28} y={17} fill="#aaa" fontSize={10}>SPY (candlestick)</text>
              <line x1={8} y1={30} x2={24} y2={30} stroke="#FF7CDE" strokeWidth={2} />
              <text x={28} y={33} fill="#aaa" fontSize={10}>Portfolio (indexed)</text>
            </g>
          )}
        </g>
      </svg>
    </div>
  );
};

const Performance = () => {
  const [performanceData, setPerformanceData]   = useState(null);
  const [tradeStats, setTradeStats]             = useState(null);
  const [ohlcData, setOhlcData]                 = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [chartLoading, setChartLoading]         = useState(false);
  const [error, setError]                       = useState(null);
  const [timeframe, setTimeframe]               = useState(90);
  const [chartType, setChartType]               = useState('candlestick'); // 'line' | 'candlestick'

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      const [chartData, stats] = await Promise.all([
        tradingAPI.getPerformanceChartData(timeframe),
        tradingAPI.getTradeStatistics(),
      ]);
      setPerformanceData(chartData.data);
      setTradeStats(stats.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  const fetchOhlc = useCallback(async () => {
    try {
      setChartLoading(true);
      const res = await tradingAPI.getSpyOhlc(timeframe);
      setOhlcData(res.data.candles || []);
    } catch {
      setOhlcData([]);
    } finally {
      setChartLoading(false);
    }
  }, [timeframe]);

  useEffect(() => { fetchPerformanceData(); }, [fetchPerformanceData]);
  useEffect(() => {
    if (chartType === 'candlestick') fetchOhlc();
  }, [chartType, fetchOhlc]);

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0);

  const formatPercent = (v) => {
    const n = parseFloat(v) || 0;
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  // Build line chart data from snapshots
  const lineData = performanceData
    ? (performanceData.dates || []).map((d, i) => ({
        date: d,
        value: performanceData.portfolio_values?.[i] ?? null,
        spy:   performanceData.spy_prices?.[i] ?? null,
      }))
    : [];


  if (loading) return <div className="loading">Loading performance data…</div>;
  if (error)   return (
    <div className="error-message">
      <h3>Error Loading Performance</h3>
      <p>{error}</p>
      <button onClick={fetchPerformanceData} className="btn btn-primary">Retry</button>
    </div>
  );

  return (
    <div className="performance-tab">

      {/* ── Controls row ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Timeframe */}
        <div className="timeframe-selector">
          {[30, 90, 180, 365].map(d => (
            <button
              key={d}
              className={`timeframe-btn ${timeframe === d ? 'active' : ''}`}
              onClick={() => setTimeframe(d)}
            >
              {d === 30 ? '1M' : d === 90 ? '3M' : d === 180 ? '6M' : '1Y'}
            </button>
          ))}
        </div>

        {/* Chart type toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => setChartType('candlestick')}
            style={{
              padding: '6px 14px', borderRadius: '6px 0 0 6px', border: '1px solid #444',
              background: chartType === 'candlestick' ? '#a78bfa' : '#1a1a2e',
              color: chartType === 'candlestick' ? '#fff' : '#aaa',
              cursor: 'pointer', fontSize: '0.85em',
            }}
          >
            📊 Candlestick
          </button>
          <button
            onClick={() => setChartType('line')}
            style={{
              padding: '6px 14px', borderRadius: '0 6px 6px 0', border: '1px solid #444',
              background: chartType === 'line' ? '#a78bfa' : '#1a1a2e',
              color: chartType === 'line' ? '#fff' : '#aaa',
              cursor: 'pointer', fontSize: '0.85em',
            }}
          >
            📈 Line
          </button>
        </div>
      </div>

      {/* ── Chart ────────────────────────────────────────────────────────── */}
      <div className="chart-section" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 12 }}>
          {chartType === 'candlestick'
            ? 'SPY Candlestick vs Portfolio Performance (pink line)'
            : 'Portfolio Value Over Time'}
        </h3>

        {chartLoading && <div style={{ color: '#a78bfa', padding: 16 }}>Loading chart…</div>}

        {!chartLoading && chartType === 'candlestick' && ohlcData.length > 0 && (
          <CandlestickSVG data={ohlcData} portfolioData={performanceData} />
        )}

        {!chartLoading && chartType === 'candlestick' && ohlcData.length === 0 && (
          <div style={{ color: '#666', padding: 16 }}>No SPY data available.</div>
        )}

        {!chartLoading && chartType === 'line' && lineData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={lineData} margin={{ top: 8, right: 16, left: 8, bottom: 24 }}>
              <defs>
                <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#a78bfa" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#222" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#888', fontSize: 11 }}
                tickFormatter={(v) => v.slice(5)}
                angle={-45}
                textAnchor="end"
                interval={Math.max(1, Math.floor(lineData.length / 8))}
              />
              <YAxis tick={{ fill: '#888', fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} width={55} />
              <Tooltip
                formatter={(v) => [formatCurrency(v), 'Portfolio Value']}
                labelFormatter={(l) => l}
                contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 6 }}
                labelStyle={{ color: '#a78bfa' }}
                itemStyle={{ color: '#ddd' }}
              />
              <Area type="monotone" dataKey="value" stroke="#a78bfa" fill="url(#portfolioGrad)" strokeWidth={2} dot={false} connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {!chartLoading && chartType === 'line' && lineData.length === 0 && (
          <div style={{ color: '#666', padding: 16 }}>
            No portfolio history yet. Performance snapshots are captured daily.
          </div>
        )}
      </div>

      {/* ── Performance Summary ───────────────────────────────────────────── */}
      {performanceData && (
        <div className="performance-summary">
          <div className="summary-card">
            <div className="card-label">Total Return</div>
            <div className={`card-value ${performanceData.total_return >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(performanceData.total_return)}
            </div>
          </div>
          <div className="summary-card">
            <div className="card-label">Annualized Return</div>
            <div className={`card-value ${performanceData.annualized_return >= 0 ? 'positive' : 'negative'}`}>
              {formatPercent(performanceData.annualized_return)}
            </div>
          </div>
          <div className="summary-card">
            <div className="card-label">Sharpe Ratio</div>
            <div className="card-value">{performanceData.sharpe_ratio?.toFixed(2) || 'N/A'}</div>
          </div>
          <div className="summary-card">
            <div className="card-label">Max Drawdown</div>
            <div className="card-value negative">{formatPercent(performanceData.max_drawdown)}</div>
          </div>
        </div>
      )}

      {/* ── Trade Statistics ──────────────────────────────────────────────── */}
      {tradeStats && tradeStats.total_trades === 0 && (
        <div style={{
          padding: '1.25rem 1.5rem',
          background: 'rgba(167,139,250,0.07)',
          border: '1px solid rgba(167,139,250,0.25)',
          borderRadius: 10,
          marginBottom: 24,
          color: 'var(--t-violet)',
          fontSize: '0.9rem',
        }}>
          <strong>No closed trades yet.</strong> Trade statistics will populate once the algorithm
          exits its first position. All 20 current positions were opened on March 27 and are still
          active — P&amp;L is tracked in the performance metrics above.
        </div>
      )}
      {tradeStats && (
        <div className="trade-statistics">
          <h3>Trade Statistics</h3>
          <div className="stats-grid">
            {[
              ['Total Trades',      tradeStats.total_trades    || 0,               ''],
              ['Winning Trades',    tradeStats.winning_trades  || 0,               'positive'],
              ['Losing Trades',     tradeStats.losing_trades   || 0,               'negative'],
              ['Win Rate',          tradeStats.win_rate ? formatPercent(tradeStats.win_rate) : 'N/A', ''],
              ['Average Win',       formatCurrency(tradeStats.avg_win),            'positive'],
              ['Average Loss',      formatCurrency(tradeStats.avg_loss),           'negative'],
              ['Best Trade',        formatCurrency(tradeStats.best_trade),         'positive'],
              ['Worst Trade',       formatCurrency(tradeStats.worst_trade),        'negative'],
              ['Total Profit',      formatCurrency(tradeStats.total_profit),       tradeStats.total_profit >= 0 ? 'positive' : 'negative'],
              ['Profit Factor',     tradeStats.profit_factor?.toFixed(2) || 'N/A',''],
              ['Avg Hold Time',     tradeStats.avg_hold_time_days ? `${tradeStats.avg_hold_time_days} days` : 'N/A', ''],
              ['Turnover Rate',     tradeStats.monthly_turnover ? formatPercent(tradeStats.monthly_turnover) : 'N/A', ''],
            ].map(([label, val, cls]) => (
              <div key={label} className="stat-card">
                <div className="stat-label">{label}</div>
                <div className={`stat-value ${cls}`}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Benchmark Comparison ──────────────────────────────────────────── */}
      {performanceData?.benchmark_return !== undefined && (
        <div className="benchmark-comparison">
          <h3>vs S&P 500 Benchmark</h3>
          <div className="comparison-grid">
            <div className="comparison-item">
              <div className="comparison-label">Your Portfolio</div>
              <div className={`comparison-value ${performanceData.total_return >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(performanceData.total_return)}
              </div>
            </div>
            <div className="comparison-item">
              <div className="comparison-label">S&P 500 (SPY)</div>
              <div className={`comparison-value ${performanceData.benchmark_return >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(performanceData.benchmark_return)}
              </div>
            </div>
            <div className="comparison-item">
              <div className="comparison-label">Outperformance</div>
              <div className={`comparison-value ${performanceData.outperformance >= 0 ? 'positive' : 'negative'}`}>
                {formatPercent(performanceData.outperformance)}
              </div>
            </div>
          </div>
        </div>
      )}

      {!performanceData && !tradeStats && (
        <div className="info-message">
          <h3>No Performance Data Yet</h3>
          <p>Performance metrics appear once you have trade history and daily snapshots.</p>
        </div>
      )}
    </div>
  );
};

export default Performance;
